<template>
  <div class="left-form">
    <img src="@/assets/images/common/drip-logo.svg" />
    <div class="spacing-32"></div>
    <h1 class="drip-h1 color-noble-blue-500 font-weight-bold">
      {{ $t('newLogin.heading') }}
    </h1>
    <div
      v-if="!otpRequested"
      class="drip-body color-noble-blue-500 font-weight-400"
    >
      {{ $t('newLogin.phoneSubHeading') }}
    </div>
    <div class="spacing-8"></div>
    <b-form v-if="!otpRequested" @submit.prevent="retrievePhoneOTP">
      <div class="status-card-container">
        <error-card
          :error="error"
          :text="errorText.text"
          :description="errorText.description"
        />
      </div>
      <div class="form-container input-container contact_code">
        <div class="input-label">
          {{ $t('newLogin.phoneForm.phoneLabel') }}
        </div>
        <div class="input-block flex-row mb-3">
          <div
            class="d-flex flex-column mr-2"
            style="width: 120px; min-width: 120px"
          >
            <input
              id="countryCode"
              type="hidden"
              :value="codeValue.countryCode"
            />
            <multiselect
              id="contact_code"
              name="contact_code"
              placeholder="eg +91"
              v-model="codeValue"
              v-validate="'required'"
              data-vv-as="Country code"
              class="drip-dropdown"
              :options="codeOptions"
              track-by="countryCode"
              label="callingCodeLabel"
              selectLabel=""
              selectedLabel=""
              deselectLabel=""
              :allow-empty="false"
            >
              <template v-slot:singleLabel="{ option }">
                <img
                  :src="
                    require(`@/assets/images/flags/${option.countryCode}.svg`)
                  "
                  width="24"
                  class="mr-2"
                  :alt="option.countryCode"
                />
                {{ option.callingCodeLabel }}
              </template>
              <template slot="option" slot-scope="{ option }">
                <img
                  :src="
                    require(`@/assets/images/flags/${option.countryCode}.svg`)
                  "
                  width="24"
                  class="mr-2"
                  :alt="option.countryCode"
                />
                {{ option.callingCodeLabel }}
              </template>
            </multiselect>
            <span v-if="errors.has('contact_code')" class="error">
              {{ errors.first('contact_code') }}
            </span>
          </div>
          <div class="d-flex flex-column" style="width: 100%">
            <input
              id="contact_phone"
              name="contact_phone"
              data-vv-as="Contact phone"
              type="text"
              :placeholder="$t('newLogin.phoneForm.placeholder')"
              v-validate="
                'required|numbers_only|max:16|valid_cc_phone:countryCode'
              "
              v-model="formData.phone"
            />
            <span v-if="errors.has('contact_phone')" class="error">
              {{ errors.first('contact_phone') }}
            </span>
          </div>
        </div>
        <action-button
          variant="solid"
          type="submit"
          extraClass="w-100 pt-3 text-center mt-0"
        >
          <b-icon-arrow-repeat
            v-if="otpRequestLoading"
            animation="spin"
            scale="0.9"
            class="mr-2"
          />
          {{ $t('newLogin.phoneForm.getOtp') }}
        </action-button>
        <p class="d-block text-center info mt-4 mb-0">
          {{ $t('newLogin.phoneForm.link2') }}
        </p>
        <div class="spacing-24"></div>
        <action-button
          variant="outline"
          extraClass="w-100 pt-3 text-center"
          :click="toggleLogin"
        >
          {{ $t('newLogin.phoneForm.emailLogin') }}
        </action-button>
      </div>
    </b-form>
    <b-form @submit.prevent="submitLoginOTP" v-else>
      <div class="status-card-container">
        <p class="d-block info mb-2 mt-2">
          {{ $t('newLogin.phoneForm.otpMsg', { phone: formData.phone }) }}
        </p>
        <a @click="changeNumber" class="d-block link mt-0 cursor-pointer">
          {{ $t('newLogin.phoneForm.numberChange') }}
        </a>
        <error-card
          :error="otpError"
          :text="$t('newLogin.otpError.text')"
          :description="$t('newLogin.otpError.description')"
        />
      </div>
      <div class="form-container">
        <div class="form-field mb-4">
          <h3 class="label">{{ $t('newLogin.phoneForm.enterOtp') }}</h3>
          <v-otp-input
            class="justify-content-around"
            ref="otpInput"
            input-classes="otp-input"
            separator=""
            data-vv-as="OTP number"
            :num-inputs="6"
            :should-auto-focus="true"
            :is-input-num="true"
            @on-change="handleOnChange"
            @on-complete="handleOnComplete"
          />
        </div>
        <action-button
          variant="solid"
          type="submit"
          :extraClass="`w-100 pt-3 text-center ${
            disableButton ? 'disabled' : ''
          }`"
        >
          <b-icon-arrow-repeat
            v-if="loading"
            animation="spin"
            scale="0.9"
            class="mr-2"
          />
          {{ $t('newLogin.phoneForm.verify') }}
        </action-button>
        <p class="d-block text-center info mt-4 mb-4">
          {{ $t('newLogin.phoneForm.link3') }}
        </p>
        <h3
          class="btn-label text-center cursor-pointer hover-on-underline"
          @click="resend"
        >
          {{ $t('newLogin.phoneForm.resendOtp') }}
        </h3>
        <div class="drip-label font-weight-bold color-noble-blue-500">
          {{ $t('newLogin.phoneForm.info') }}
        </div>
        <div class="spacing-24"></div>
        <action-button
          variant="outline"
          extraClass="pt-3 text-center"
          :click="toggleLogin"
        >
          {{ $t('newLogin.phoneForm.emailLogin') }}
        </action-button>
      </div>
    </b-form>
  </div>
</template>
<script>
import Multiselect from 'vue-multiselect'
import userService from '@/services/user'
import accountService from '@/services/accounts'
import ErrorCard from '@/components/validation/ErrorCard'
import ActionButton from '@/components/common/ActionButton'
import VOtpInput from '@bachdgvn/vue-otp-input'
import Cookies from 'js-cookie'
import { required } from 'vee-validate/dist/rules.esm'
import { APP_TYPES } from '@/utils/constants'
import { captureException } from '@sentry/vue'
import { getSortedCountries, verifyPhoneNumber } from '@/utils/common'

export default {
  components: {
    ActionButton,
    ErrorCard,
    VOtpInput,
    Multiselect
  },
  data() {
    return {
      loading: false,
      otpRequestLoading: false,
      otpRequested: false,
      formData: {
        phone: '',
        cc: '',
        password: ''
      },
      codeValue: '',
      codeOptions: getSortedCountries(),
      disableButton: true,
      publicToken: '',
      error: false,
      otpError: false
    }
  },
  created() {
    this.$validator.extend('remove_specials', {
      ...required,
      validate(value) {
        return /^[a-zA-Z0-9\s]*$/.test(value)
      }
    })
    this.$validator.extend('numbers_only', {
      ...required,
      validate(value) {
        return /^[0-9]*$/.test(value)
      }
    })
  },
  mounted() {
    this.$validator.extend('valid_cc_phone', {
      validate(value, [otherInput]) {
        const countryCode = document.getElementById(otherInput)?.value
        if (value && value.length > 3 && value.length < 16 && countryCode) {
          return verifyPhoneNumber(value, countryCode)
        }
        return false
      },
      params: ['otherInput']
    })
  },
  methods: {
    clearExistingUserDetails() {
      this.$store.dispatch('unsetLocale')
      this.$store.dispatch('unsetAccountId')
    },
    toggleLogin() {
      this.$emit('toggle')
    },
    handleOnChange(value) {
      this.otpError = false
      this.disableButton = value.length !== 6
    },
    handleOnComplete(value) {
      this.formData.password = value
    },
    resend() {
      this.retrievePhoneOTP()
    },
    retrievePhoneOTP() {
      this.$validator.validate().then((result) => {
        if (result) {
          this.otpRequestLoading = true
          this.$mpTrack('Phone Login - Sent OTP to phone')
          userService
            .fetchAccessToken()
            .then((response) => {
              userService
                .getOTPbyPhone(response.data.token, {
                  user: {
                    phone: this.formData.phone,
                    country_code: this.codeValue?.callingCode
                  }
                })
                .then(() => {
                  this.$mpTrack('Phone Login - Sent OTP to phone success')
                  this.loading = false
                  this.otpRequested = true
                  this.otpRequestLoading = false
                  this.$notify({
                    title: this.$t('notifications.otp.title'),
                    text: this.$t('notifications.otp.text'),
                    type: 'success'
                  })
                })
                .catch((e) => {
                  this.$mpTrack('Phone Login - Failed to send OTP')
                  console.log(e)
                  this.otpRequestLoading = false
                  this.error = true
                })
            })
            .catch((e) => {
              console.log(e)
              this.$mpTrack('Phone Login - Failed to send OTP')
              this.otpRequestLoading = false
              this.error = true
            })
        }
      })
    },
    submitLoginOTP() {
      if (this.formData.password) {
        this.loading = true
        this.$mpTrack('Phone Login - Add OTP to input')
        return userService
          .fetchAccessToken()
          .then((response) => {
            accountService
              .signInAccount(response.data.token, {
                phone: this.formData.phone,
                country_code: this.codeValue?.callingCode,
                password: this.formData.password
              })
              .then((res) => {
                if (
                  res &&
                  res.data &&
                  res.data.message &&
                  res.data.message.token
                ) {
                  this.$mpTrack('Phone Login - Add OTP to input success')
                  Cookies.set('default_auth_token', res.data.message.token, {
                    domain: '.dripcapital.com'
                  })
                  localStorage.setItem(
                    'default_auth_token',
                    res.data.message.token
                  )
                  this.handleRedirect(res.data)
                } else {
                  console.log(res)
                  this.$mpTrack('Phone Login - Session generation failed', {
                    form: 'phone',
                    phone: this.formData.phone
                  })

                  this.$notify({
                    title: this.$t('error.unableToProcess'),
                    type: 'error'
                  })
                }
                this.stopLoading()
              })
              .catch((err) => {
                this.stopLoading(err)
                const error = err?.response?.data || err?.message || err
                this.$mpTrack('Phone Login - Session generation failed', {
                  error: error,
                  phone: this.formData.phone
                })
                this.$notify({
                  title: this.$t('error.unableToProcess'),
                  type: 'error'
                })
                console.log(err)
              })
          })
          .catch((err) => {
            this.stopLoading(err)
            this.$mpTrack('Phone Login - Failed to login')
            this.$notify({
              title: this.$t('error.unableToProcess'),
              type: 'error'
            })
            console.log(err)
          })
      }
    },
    filterCallbackLink(callbackUrl) {
      if (['/logout', '/login'].includes(callbackUrl)) {
        return '/'
      }
      return callbackUrl
    },
    async handleRedirect(sessionData) {
      let errorMessage = ''
      try {
        const callback = this.$route.query.callback
        const application = this.$route.query.app

        const userType = sessionData.utype
        const userTarget = sessionData.target

        const isUserTypeLegit =
          ['onboarding', 'portal'].includes(userTarget) &&
          ['channel_partner', 'super_user', 'portal_user'].includes(userType)

        this.$store.dispatch('setUserType', sessionData)
        const userResponse = await accountService.fetchLoggedUser(
          sessionData.message.token
        )
        const userData = userResponse.data

        let account = userData && userData.linked_users[0]

        if (!account && userTarget !== 'onboarding') {
          console.error('Business account not linked!')
          errorMessage = 'Business account not linked!'
          this.$notify({
            title: this.$t('error.unableToProcess'),
            type: 'error'
          })
          this.$mpTrack('Phone Login - Business account not linked!', {
            target: sessionData.target,
            type: sessionData.utype,
            email: this.formData.phone
          })
          return
        }
        if (this.$route.query.user_id) {
          userData.link_id = this.$route.query.user_id
        }
        this.$store.dispatch('setUser', userData)
        localStorage.setItem('user', JSON.stringify(userData))
        this.$mpTrack('Phone Login - Login Successful')
        let redirectTo = '/'
        if (this.redirectToDripOne) {
          redirectTo = '/insights'
        }

        // admin log in using user id params
        if (this.$route.query.user_id) {
          this.$mpTrack('Login - Logged as admin user')
          this.$router.push({
            path: redirectTo,
            query: { user_id: this.$route.query.user_id }
          })
          return
        }

        if (!isUserTypeLegit) {
          console.error('User not authorized to login!')
          errorMessage = 'User not authorized to login!'
          this.$notify({
            title: this.$t('error.unauthorizedToolTip'),
            type: 'error'
          })
          this.$mpTrack('Phone Login - Type not mentioned!', {
            target: sessionData.target,
            type: sessionData.utype,
            email: this.formData.phone
          })
          return
        }

        const { productType, userId } = this.isProductType(
          userData,
          userTarget,
          userType
        )

        // redirects to Portal SCF
        if (productType === APP_TYPES.SCF) {
          this.$mpTrack('Phone Login - Redirected to SCF Portal', {
            phone: this.formData.phone
          })
          Cookies.set('default_auth_token', sessionData.message.token, {
            domain: '.dripcapital.com'
          })
          Cookies.set('user_id', userData.linked_users[0].link_id, {
            domain: '.dripcapital.com'
          })
          const redirectUrl =
            process.env.VUE_APP_DRIP_SCF_PORTAL_HOST +
            (application === APP_TYPES.PSCF ? callback : '')
          window.location.href = redirectUrl
          return
        }

        // redirects to Channel Partner
        if (productType === APP_TYPES.CP) {
          this.$mpTrack('Phone Login - Redirected to CP Portal', {
            phone: this.formData.phone
          })
          Cookies.set('user_id', userId, { domain: '.dripcapital.com' })
          Cookies.set('token', sessionData.message.token, {
            domain: '.dripcapital.com'
          })
          const redirectUrl =
            process.env.VUE_APP_CP_HOST +
            (application === APP_TYPES.CP ? callback : '')
          window.location.href = redirectUrl
          return
        }

        // redirects to Portal NDAJ
        if (productType === APP_TYPES.NDAJ) {
          this.$mpTrack('Phone Login - Redirected to NDAJ Portal', {
            phone: this.formData.phone
          })
          Cookies.set('user_id', userId, { domain: '.dripcapital.com' })
          Cookies.set('token', sessionData.message.token, {
            domain: '.dripcapital.com'
          })
          const redirectUrl =
            process.env.VUE_APP_NDAJ_HOST +
            (application === APP_TYPES.NDAJ ? callback : '')
          window.location.href = redirectUrl
          return
        }

        // redirects to legal document as the approve stage requires a legal esign agreement
        if (
          account.country === 'India' &&
          account.role === 'exporters' &&
          account.status === 'approved' &&
          !account.legal_agreements_esigned
        ) {
          this.$mpTrack('Phone Login - Redirected to RF Portal Legal', {
            phone: this.formData.phone
          })
          this.$router.push('/legal-sign/')
          return
        }

        // redirects to Portal RF
        if (application === APP_TYPES.RFP && callback) {
          this.$mpTrack('Phone Login - Redirected to RF Portal', {
            phone: this.formData.phone,
            link: callback
          })
          this.$router.push(this.filterCallbackLink(callback))
          return
        }

        this.$mpTrack('Phone Login - Redirected to RF Portal', {
          link: 'default',
          phone: this.formData.phone
        })
        this.$router.push('/')
        return
      } catch (e) {
        console.log(e)
        this.$mpTrack('Phone Login - Failed to redirect session', {
          phone: this.formData.phone || '',
          target: sessionData.target,
          message: errorMessage,
          page: 'login-session-code',
          mfSessionId: window.mouseflow && window.mouseflow.getSessionId()
        })
        this.clearExistingUserDetails()
        captureException(new Error(e), {
          level: 'error',
          tags: {
            phone: this.formData.phone ? this.formData.phone : '',
            target: sessionData.target,
            message: errorMessage,
            page: 'login-session-code-phone',
            mfSessionId: window.mouseflow && window.mouseflow.getSessionId()
          }
        })
        this.$notify({
          title: this.$t('error.unableToProcess'),
          type: 'error'
        })
      } finally {
        this.isLoading = false
      }
    },
    isProductType(user, userTarget, userType) {
      // for leads
      if (userTarget === 'onboarding' && userType === 'portal_user') {
        return {
          productType: APP_TYPES.NDAJ,
          userId: user.link_id
        }
      }
      // for channel partner
      if (userTarget === 'onboarding' && userType === 'channel_partner') {
        return {
          productType: APP_TYPES.CP,
          userId: user.id
        }
      }
      // for BDMs
      if (this.$store.state.account.userType === 'super_user') {
        return {
          productType: APP_TYPES.NDAJ,
          userId: user.id
        }
      }
      // for SCF users
      if (
        user.linked_users &&
        user.linked_users.length &&
        user.linked_users[0].debtor_type === 'scf'
      ) {
        return {
          productType: APP_TYPES.SCF,
          userId: user.linked_users[0].link_id
        }
      }
      return { productType: APP_TYPES.RFP, userId: '' }
    },
    stopLoading(err) {
      if (
        err &&
        err.response &&
        err.response.status &&
        err.response.status === 422
      ) {
        this.otpError = true
      }
      this.loading = false
    },
    changeNumber(e) {
      e.preventDefault()
      this.otpRequested = false
      this.formData.phone = ''
      this.$emit('resetPhone')
    }
  },
  computed: {
    errorText() {
      return {
        text: this.$t('newLogin.phoneError.text'),
        description: this.$t('newLogin.phoneError.description', {
          phone: this.formData.phone
        })
      }
    }
  }
}
</script>
<style lang="scss">
@import '@/assets/scss/_variables.scss';
.otp-input {
  width: 3.2rem;
  height: 3.2rem;
  padding: 0.3rem;
  margin: 0 0.6rem;
  font-size: 1.25rem;
  border-radius: 0.25rem;
  border: 1px solid rgba(0, 0, 0, 0.3);
  text-align: center;
  &.error {
    border: 1px solid red !important;
  }
}
.otp-input::-webkit-inner-spin-button,
.otp-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>
<style lang="scss" scoped>
@import '~vue-multiselect/dist/vue-multiselect.min.css';
.input-container {
  width: 100%;
  margin-bottom: 24px;
  .input-label {
    font-size: 14px;
    font-weight: 400;
    line-height: 19px;
    letter-spacing: 0em;
    text-align: left;
    color: $noble-blue-500;
    margin-bottom: 8px;
  }
  .input-block {
    display: flex;
    flex-direction: column;
    .drip-dropdown {
      height: 56px;
      width: 100%;
      max-width: 400px;
      ::v-deep .multiselect__select {
        height: 100%;
        &::before {
          z-index: 1;
        }
      }
      ::v-deep .multiselect__tags {
        outline: 0px;
        height: 56px;
        padding: 16px;
        padding-right: 24px;
        border-radius: 8px;
        border: 1px solid $noble-blue-500;
        color: $neutral-gray-500;
        font-weight: 400;
        margin-bottom: 4px;
        .multiselect__placeholder {
          color: $neutral-gray-500;
        }
        .multiselect__single {
          display: flex;
          color: $noble-blue-500;
          font-weight: 700;
          vertical-align: bottom;
        }
      }
    }
    input:-webkit-autofill,
    input:-webkit-autofill:hover,
    input:-webkit-autofill:focus,
    input:-webkit-autofill:active {
      -webkit-box-shadow: 0 0 0 30px white inset !important;
    }
    input {
      outline: 0px;
      width: 100%;
      max-width: 400px;
      height: 56px;
      padding: 16px;
      border-radius: 8px;
      border: 1px solid $noble-blue-500;
      background: $white;
      color: $noble-blue-500 !important;
      font-size: 16px;
      font-weight: 700;
      &::placeholder {
        font-weight: 400;
        background: $white;
        color: $neutral-gray-500;
      }
      &.width-800 {
        max-width: 800px;
      }
    }
  }
}
</style>
